import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import type { CertGapResponse, CertGapRow } from '../types';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function statusTone(status: CertGapRow['status']): 'ok' | 'missing' | 'expired' {
  if (status === 'OK') return 'ok';
  if (status === 'EXPIRED') return 'expired';
  return 'missing';
}

function statusLabel(status: CertGapRow['status']): string {
  if (status === 'OK') return 'OK';
  if (status === 'EXPIRED') return 'Expired';
  return 'Missing';
}

function TableHead() {
  return (
    <table className="cg-print-table cg-print-table-head">
      <thead>
        <tr>
          <th className="cg-print-col-no">No.</th>
          <th className="cg-print-col-name">Crew</th>
          <th className="cg-print-col-rank">Rank</th>
          <th className="cg-print-col-nat">Nationality</th>
          <th className="cg-print-col-cert">Certificate</th>
          <th className="cg-print-col-status">Status</th>
          <th className="cg-print-col-date">Expired</th>
        </tr>
      </thead>
    </table>
  );
}

function LineRow({ row, index }: { row: CertGapRow; index: number }) {
  const tone = statusTone(row.status);
  return (
    <table className={`cg-print-table cg-print-row-table cg-print-row-${tone}`}>
      <tbody>
        <tr>
          <td className="cg-print-col-no">{index + 1}</td>
          <td className="cg-print-col-name">{row.name}</td>
          <td className="cg-print-col-rank">{dash(row.rank)}</td>
          <td className="cg-print-col-nat">{dash(row.nationality)}</td>
          <td className="cg-print-col-cert">{dash(row.certificate)}</td>
          <td className="cg-print-col-status">
            <span className={`cg-print-pill cg-print-pill-${tone}`}>{statusLabel(row.status)}</span>
          </td>
          <td className="cg-print-col-date">{fmtDate(row.expiredDate)}</td>
        </tr>
      </tbody>
    </table>
  );
}

export type CertGapsReportProps = {
  data: CertGapResponse;
  rows: CertGapRow[];
  tabLabel: string;
};

export const CertGapsReport: React.FC<CertGapsReportProps> = ({ data, rows, tabLabel }) => {
  const vessel = data.vessel;
  const vesselName = vessel?.vesselName || '—';
  const summary = data.summary;

  const firstHeader = useMemo(
    () => (
      <div className="cg-print-flow-header">
        <div className="cg-print-accent" aria-hidden />
        <ReportHeader>
          <ReportTitleRow title="Document Gaps" formId="Rank requirements" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselName} />
              <ReportField label="Abrv" value={dash(vessel?.vesselAbrv)} />
              <ReportField label="Filter" value={tabLabel} />
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
            </ReportMetaRow>
          </ReportMeta>
          <div className="cg-print-kpis">
            <div className="cg-print-kpi">
              <span className="cg-print-kpi-label">Crew</span>
              <span className="cg-print-kpi-value">{summary.crewCount}</span>
            </div>
            <div className="cg-print-kpi cg-print-kpi-gaps">
              <span className="cg-print-kpi-label">Gaps</span>
              <span className="cg-print-kpi-value">{summary.gapCount}</span>
            </div>
            <div className="cg-print-kpi">
              <span className="cg-print-kpi-label">OK</span>
              <span className="cg-print-kpi-value">{summary.okCount}</span>
            </div>
            <div className="cg-print-kpi">
              <span className="cg-print-kpi-label">Shown</span>
              <span className="cg-print-kpi-value">{rows.length}</span>
            </div>
          </div>
        </ReportHeader>
        <TableHead />
      </div>
    ),
    [rows.length, summary, tabLabel, vessel?.vesselAbrv, vesselName],
  );

  const continueHeader = useMemo(
    () => (
      <div className="cg-print-flow-header">
        <div className="cg-print-continue-bar">
          <span className="cg-print-continue-title">Document Gaps</span>
          <span className="cg-print-continue-vessel">{vesselName}</span>
          <span className="cg-print-continue-hint">continued</span>
        </div>
        <TableHead />
      </div>
    ),
    [vesselName],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>
          Compares onboard crew certificates against rank requirements for this vessel.
        </ReportNote>
      </ReportFooter>
    ),
    [],
  );

  const { items, itemKeys } = useMemo(() => {
    if (rows.length === 0) {
      return {
        items: [
          <table key="empty" className="cg-print-table cg-print-row-table">
            <tbody>
              <tr>
                <td colSpan={7} className="cg-print-empty">
                  No certificate gap rows for this filter.
                </td>
              </tr>
            </tbody>
          </table>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: rows.map((row, i) => (
        <LineRow key={`${row.crewId}-${row.certCatalogId}`} row={row} index={i} />
      )),
      itemKeys: rows.map((row) => `${row.crewId}-${row.certCatalogId}`),
    };
  }, [rows]);

  return (
    <ReportFlowPages
      className="cert-gaps-print"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default CertGapsReport;
